/**
 * Loader state: the network dataset, what-if active sets, globe filters,
 * timeframe, view settings, analysis results and the live AOS/LOS log.
 *
 * Selection lives in the shell store (useUI.selection) so the detail panel,
 * strip and globe agree across modes. The clock lives in useClock.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ANALYSIS, TIMEFRAME } from './constants';
import type { AnalysisResults } from './analysis/types';
import type { LiveTickResult } from './engine/orbit.core';
import type { MapStyle, OrbitPathMode, SceneMode } from './globe/GlobeEngine';
import type { Mission, NetworkDataset, NetworkId } from './types';

export type AnalysisStatus = 'idle' | 'running' | 'done' | 'error';
/** 'full' is the whole loader; 'noaa-core' is the slim NOAA-only instance (network locked, no live layers). */
export type LoaderProfile = 'full' | 'noaa-core';
export type DatasetStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AnalysisProgress {
  percent: number;
  message: string;
}

export interface Timeframe {
  hours: number;
  fiscalYear: number;
  startMs: number;
  endMs: number;
}

export interface AosLosEntry {
  id: number;
  type: 'AOS' | 'LOS';
  missionId: string;
  antennaId: string;
  timeMs: number;
  elevation?: number;
  durationMs?: number;
  maxElevation?: number;
}

export interface SunOutageBadge {
  antennaId: string;
  missionId: string;
  peakMs: number;
  durationSec: number;
  separationDeg: number;
}

export interface ActiveContact {
  missionId: string;
  antennaId: string;
  sinceMs: number;
  elevation: number;
}

/** Scene defaults per network, as the previous app applied them on a mode switch. */
export function defaultSceneModeFor(network: NetworkId): SceneMode {
  return network === 'full_catalogue' ? '3D' : '2D';
}

/** Networks with planet navigation (Moon or Mars viewers, DSN Now). */
export function isDeepSpaceNetwork(network: NetworkId): boolean {
  return network === 'artemis' || network === 'mars';
}

/** Missions the analysis starts with: everything, or the first 100 by NORAD id for large networks. */
export function defaultActiveMissionIds(missions: Mission[]): string[] {
  if (missions.length <= ANALYSIS.LARGE_NETWORK_THRESHOLD) return missions.map((m) => m.id);
  const sorted = [...missions].sort((a, b) => {
    const na = Number(a.noradId);
    const nb = Number(b.noradId);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.id.localeCompare(b.id);
  });
  return sorted.slice(0, ANALYSIS.DEFAULT_TRACKED_SUBSET).map((m) => m.id);
}

let aosLosSeq = 0;
const AOSLOS_MAX = 100;

interface LoaderState {
  network: NetworkId;
  dataset: NetworkDataset | null;
  datasetStatus: DatasetStatus;
  datasetRetry: number;
  datasetError: string | null;
  /** Counts fresh dataset loads (setDataset); patches leave it alone. */
  datasetEpoch: number;
  /** Server time of the last sync poll (ISO), the next `since`. */
  syncSince: string | null;

  /** What-if sets fed to the analysis. */
  activeMissionIds: string[];
  activeAntennaIds: string[];
  /** Globe visibility filters (null means everything). They do not change the analysis. */
  visibleMissionIds: string[] | null;
  visibleAntennaIds: string[] | null;

  timeframe: Timeframe;
  /** Analysis toggles from D1 (feature_toggles); unknown ids count as enabled. */
  toggles: Record<string, boolean>;

  analysisStatus: AnalysisStatus;
  analysisProgress: AnalysisProgress | null;
  analysisError: string | null;
  results: AnalysisResults | null;
  /** Monotonic counter: bump to force a re-run. */
  analysisRequest: number;

  // View settings (persisted)
  sceneMode: SceneMode;
  mapStyle: MapStyle;
  dayNight: boolean;
  orbitPathMode: OrbitPathMode;
  coverageEnabled: boolean;
  coneMode: boolean;
  contactLinesEnabled: boolean;
  coverageRingsEnabled: boolean;
  nightLights: boolean;
  google3D: boolean;
  osmBuildings: boolean;
  terrain: boolean;
  tdrssShown: boolean;
  weatherLayer: string | null;
  weatherOpacity: number;
  auroraEnabled: boolean;
  gpsJamEnabled: boolean;
  conjunctionsEnabled: boolean;
  launchesEnabled: boolean;
  spaceWeatherEnabled: boolean;
  satnogsEnabled: boolean;
  overlays: string[];
  horizonsBodies: string[];

  trackedMissionId: string | null;
  activeContacts: ActiveContact[];
  inContact: number;
  aosLos: AosLosEntry[];
  aosLosFilter: string | null;
  sunOutage: SunOutageBadge | null;
  guamDemoActive: boolean;
  profile: LoaderProfile;
  /** Re-run the analysis when the clock jumps by an hour or more. */
  autoRerunOnJump: boolean;

  setNetwork: (network: NetworkId) => void;
  setDatasetLoading: () => void;
  setDataset: (dataset: NetworkDataset) => void;
  setDatasetError: (message: string) => void;
  patchDataset: (patch: Partial<NetworkDataset>) => void;
  setSyncSince: (iso: string) => void;
  setActiveMissionIds: (ids: string[]) => void;
  setActiveAntennaIds: (ids: string[]) => void;
  toggleActiveMission: (id: string) => void;
  toggleActiveAntenna: (id: string) => void;
  setVisibleMissionIds: (ids: string[] | null) => void;
  setVisibleAntennaIds: (ids: string[] | null) => void;
  setTimeframeHours: (hours: number, nowMs: number) => void;
  setTimeframeStart: (startMs: number) => void;
  setFiscalYear: (fiscalYear: number) => void;
  setToggle: (id: string, enabled: boolean) => void;
  isToggleEnabled: (id: string) => boolean;
  requestAnalysis: () => void;
  setAnalysisRunning: (progress?: AnalysisProgress) => void;
  setAnalysisProgress: (progress: AnalysisProgress) => void;
  setAnalysisResults: (results: AnalysisResults) => void;
  setAnalysisError: (message: string) => void;
  setView: (patch: Partial<LoaderViewSettings>) => void;
  toggleOverlay: (key: string) => void;
  toggleHorizonsBody: (key: string) => void;
  setTracked: (missionId: string | null) => void;
  applyLiveTick: (tick: LiveTickResult) => void;
  resetLiveContacts: () => void;
  trimAosLosAfter: (ms: number) => void;
  setAosLosFilter: (missionId: string | null) => void;
  clearAosLos: () => void;
  setSunOutage: (badge: SunOutageBadge | null) => void;
  setGuamDemoActive: (active: boolean) => void;
  setProfile: (profile: LoaderProfile) => void;
  setAutoRerunOnJump: (v: boolean) => void;
}

export type LoaderViewSettings = Pick<
  LoaderState,
  | 'sceneMode'
  | 'mapStyle'
  | 'dayNight'
  | 'orbitPathMode'
  | 'coverageEnabled'
  | 'coneMode'
  | 'contactLinesEnabled'
  | 'coverageRingsEnabled'
  | 'nightLights'
  | 'google3D'
  | 'osmBuildings'
  | 'terrain'
  | 'tdrssShown'
  | 'weatherLayer'
  | 'weatherOpacity'
  | 'auroraEnabled'
  | 'gpsJamEnabled'
  | 'conjunctionsEnabled'
  | 'launchesEnabled'
  | 'spaceWeatherEnabled'
  | 'satnogsEnabled'
>;

function initialTimeframe(nowMs: number): Timeframe {
  return {
    hours: TIMEFRAME.DEFAULT_HOURS,
    fiscalYear: TIMEFRAME.DEFAULT_FY,
    startMs: nowMs,
    endMs: nowMs + TIMEFRAME.DEFAULT_HOURS * 3_600_000,
  };
}

function toggleId(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export const useLoader = create<LoaderState>()(
  persist(
    (set, get) => ({
      network: 'noaa',
      dataset: null,
      datasetStatus: 'idle',
      datasetRetry: 0,
      datasetError: null,
      datasetEpoch: 0,
      syncSince: null,
      activeMissionIds: [],
      activeAntennaIds: [],
      visibleMissionIds: null,
      visibleAntennaIds: null,
      timeframe: initialTimeframe(Date.now()),
      toggles: {},
      analysisStatus: 'idle',
      analysisProgress: null,
      analysisError: null,
      results: null,
      analysisRequest: 0,

      sceneMode: '2D',
      mapStyle: 'satellite',
      dayNight: false,
      orbitPathMode: 'selected',
      coverageEnabled: true,
      coneMode: true,
      contactLinesEnabled: true,
      coverageRingsEnabled: true,
      nightLights: false,
      google3D: false,
      osmBuildings: false,
      terrain: false,
      tdrssShown: false,
      weatherLayer: null,
      weatherOpacity: 0.7,
      auroraEnabled: false,
      gpsJamEnabled: false,
      conjunctionsEnabled: false,
      launchesEnabled: false,
      spaceWeatherEnabled: false,
      satnogsEnabled: false,
      overlays: [],
      horizonsBodies: [],

      trackedMissionId: null,
      activeContacts: [],
      inContact: 0,
      aosLos: [],
      aosLosFilter: null,
      sunOutage: null,
      guamDemoActive: false,
      profile: 'full',
      autoRerunOnJump: true,

      setNetwork: (network) =>
        set((s) =>
          s.network === network
            ? {}
            : {
                network,
                dataset: null,
                datasetStatus: 'idle',
                datasetError: null,
                syncSince: null,
                results: null,
                analysisStatus: 'idle',
                analysisError: null,
                activeMissionIds: [],
                activeAntennaIds: [],
                visibleMissionIds: null,
                visibleAntennaIds: null,
                sceneMode: defaultSceneModeFor(network),
                mapStyle: network === 'full_catalogue' ? 'dark' : 'satellite',
                orbitPathMode: 'selected',
                trackedMissionId: null,
                activeContacts: [],
                inContact: 0,
                aosLos: [],
                sunOutage: null,
                guamDemoActive: false,
                tdrssShown: !['noaa', 'noaa_extended'].includes(network) && !isLiveNetwork(network),
              },
        ),
      setDatasetLoading: () => set({ datasetStatus: 'loading', datasetError: null }),
      setDataset: (dataset) => {
        const toggles: Record<string, boolean> = {};
        for (const t of dataset.toggles) toggles[t.id] = t.enabled;
        set({
          dataset,
          datasetStatus: 'ready',
          datasetError: null,
          datasetEpoch: get().datasetEpoch + 1,
          toggles,
          activeMissionIds: dataset.network === 'full_catalogue' ? dataset.missions.map((m) => m.id) : defaultActiveMissionIds(dataset.missions),
          // The mission browser is the single display filter. Only the GOES
          // family starts unticked in NOAA; EWS and other GEO families remain visible.
          visibleMissionIds:
            dataset.network === 'noaa'
              ? dataset.missions.filter((m) => m.catalogue?.family !== 'GOES').map((m) => m.id)
              : null,
          activeAntennaIds: dataset.antennas.filter((a) => a.enabled !== false).map((a) => a.id),
          analysisRequest: dataset.network === 'full_catalogue' ? 0 : get().analysisRequest + 1,
        });
      },
      setDatasetError: (message) => set({ datasetStatus: 'error', datasetError: message }),
      patchDataset: (patch) => {
        const current = get().dataset;
        if (!current) return;
        const next = { ...current, ...patch };
        const update: Partial<LoaderState> = { dataset: next };
        // Keep the what-if selection across patches (sync edits, terrain masks); new records join it.
        if (patch.antennas) {
          const prevActive = new Set(get().activeAntennaIds);
          const prevIds = new Set(current.antennas.map((a) => a.id));
          update.activeAntennaIds = patch.antennas
            .filter((a) => prevActive.has(a.id) || (!prevIds.has(a.id) && a.enabled !== false))
            .map((a) => a.id);
        }
        if (patch.missions) {
          const prevActive = new Set(get().activeMissionIds);
          const prevIds = new Set(current.missions.map((m) => m.id));
          update.activeMissionIds = patch.missions
            .filter((m) => prevActive.has(m.id) || !prevIds.has(m.id))
            .map((m) => m.id);
        }
        if (patch.toggles) {
          const toggles: Record<string, boolean> = {};
          for (const t of patch.toggles) toggles[t.id] = t.enabled;
          update.toggles = toggles;
        }
        set({ ...update, analysisRequest: get().analysisRequest + 1 });
      },
      setSyncSince: (iso) => set({ syncSince: iso }),
      setActiveMissionIds: (ids) => set({ activeMissionIds: ids }),
      setActiveAntennaIds: (ids) => set({ activeAntennaIds: ids }),
      toggleActiveMission: (id) => set((s) => ({ activeMissionIds: toggleId(s.activeMissionIds, id) })),
      toggleActiveAntenna: (id) => set((s) => ({ activeAntennaIds: toggleId(s.activeAntennaIds, id) })),
      setVisibleMissionIds: (ids) => set({ visibleMissionIds: ids }),
      setVisibleAntennaIds: (ids) => set({ visibleAntennaIds: ids }),
      setTimeframeHours: (hours, nowMs) =>
        set((s) => ({
          timeframe: { ...s.timeframe, hours, startMs: nowMs, endMs: nowMs + hours * 3_600_000 },
          analysisRequest: s.analysisRequest + 1,
        })),
      setTimeframeStart: (startMs) =>
        set((s) => ({
          timeframe: { ...s.timeframe, startMs, endMs: startMs + s.timeframe.hours * 3_600_000 },
          analysisRequest: s.analysisRequest + 1,
        })),
      setFiscalYear: (fiscalYear) => set((s) => ({ timeframe: { ...s.timeframe, fiscalYear } })),
      setToggle: (id, enabled) =>
        set((s) => ({ toggles: { ...s.toggles, [id]: enabled }, analysisRequest: s.analysisRequest + 1 })),
      isToggleEnabled: (id) => get().toggles[id] ?? true,
      requestAnalysis: () => set((s) => ({ analysisRequest: s.analysisRequest + 1 })),
      setAnalysisRunning: (progress) =>
        set({
          analysisStatus: 'running',
          analysisProgress: progress ?? { percent: 0, message: 'Starting analysis' },
          analysisError: null,
        }),
      setAnalysisProgress: (progress) => set({ analysisProgress: progress }),
      setAnalysisResults: (results) =>
        set({ results, analysisStatus: 'done', analysisProgress: null, analysisError: null }),
      setAnalysisError: (message) =>
        set({ analysisStatus: 'error', analysisError: message, analysisProgress: null }),
      setView: (patch) => set(patch),
      toggleOverlay: (key) => set((s) => ({ overlays: toggleId(s.overlays, key) })),
      toggleHorizonsBody: (key) => set((s) => ({ horizonsBodies: toggleId(s.horizonsBodies, key) })),
      setTracked: (missionId) => set({ trackedMissionId: missionId }),
      applyLiveTick: (tick) =>
        set((s) => {
          const sameContacts =
            s.activeContacts.length === tick.contacts.length &&
            tick.contacts.every(
              (c, i) =>
                s.activeContacts[i].missionId === c.satId &&
                s.activeContacts[i].antennaId === c.antId &&
                s.activeContacts[i].sinceMs === c.sinceMs,
            );
          if (
            tick.aos.length === 0 &&
            tick.los.length === 0 &&
            sameContacts &&
            s.inContact === tick.inContact
          )
            return {};
          const entries: AosLosEntry[] = [];
          const contacts = sameContacts
            ? s.activeContacts
            : tick.contacts.map((c) => ({
                missionId: c.satId,
                antennaId: c.antId,
                sinceMs: c.sinceMs,
                elevation: c.elevation,
              }));
          if (tick.los.length) {
            for (const l of tick.los) {
              entries.push({
                id: ++aosLosSeq,
                type: 'LOS',
                missionId: l.satId,
                antennaId: l.antId,
                timeMs: tick.timestamp,
                durationMs: l.durationMs,
                maxElevation: l.maxElevation,
              });
            }
          }
          if (tick.aos.length) {
            for (const a of tick.aos) {
              entries.push({
                id: ++aosLosSeq,
                type: 'AOS',
                missionId: a.satId,
                antennaId: a.antId,
                timeMs: tick.timestamp,
                elevation: a.elevation,
              });
            }
          }
          const aosLos = entries.length ? [...entries.reverse(), ...s.aosLos].slice(0, AOSLOS_MAX) : s.aosLos;
          return { activeContacts: contacts, inContact: tick.inContact, aosLos };
        }),
      resetLiveContacts: () => set({ activeContacts: [], inContact: 0 }),
      trimAosLosAfter: (ms) =>
        set((s) => ({ aosLos: s.aosLos.filter((e) => e.timeMs <= ms), activeContacts: [], inContact: 0 })),
      setAosLosFilter: (missionId) => set({ aosLosFilter: missionId }),
      clearAosLos: () => set({ aosLos: [] }),
      setSunOutage: (badge) => set({ sunOutage: badge }),
      setGuamDemoActive: (active) => set({ guamDemoActive: active }),
      setProfile: (profile) => set(profile === 'noaa-core' ? { profile, network: 'noaa' } : { profile }),
      setAutoRerunOnJump: (autoRerunOnJump) => set({ autoRerunOnJump }),
    }),
    {
      name: 'gemdt-loader',
      partialize: (s) => ({
        dayNight: s.dayNight,
        coverageEnabled: s.coverageEnabled,
        coneMode: s.coneMode,
        contactLinesEnabled: s.contactLinesEnabled,
        coverageRingsEnabled: s.coverageRingsEnabled,
        nightLights: s.nightLights,
        google3D: s.google3D,
        osmBuildings: s.osmBuildings,
        terrain: s.terrain,
        weatherOpacity: s.weatherOpacity,
        autoRerunOnJump: s.autoRerunOnJump,
        timeframe: { hours: s.timeframe.hours, fiscalYear: s.timeframe.fiscalYear },
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<LoaderState> & { timeframe?: Partial<Timeframe> };
        const hours = p.timeframe?.hours ?? current.timeframe.hours;
        const now = Date.now();
        return {
          ...current,
          ...p,
          // Each new app session starts Loader on NOAA, including older saved demo selections.
          network: current.network,
          // Presentation choices are scoped to this visit. Discard older global
          // dark/all-path preferences when starting the default NOAA loadout.
          mapStyle: current.network === 'full_catalogue' ? 'dark' : 'satellite',
          orbitPathMode: 'selected',
          timeframe: {
            hours,
            fiscalYear: p.timeframe?.fiscalYear ?? current.timeframe.fiscalYear,
            startMs: now,
            endMs: now + hours * 3_600_000,
          },
          sceneMode: defaultSceneModeFor(current.network),
        };
      },
    },
  ),
);

/** Networks whose missions come from live TLE feeds rather than D1 or static JSON. */
export function isLiveNetwork(network: NetworkId): boolean {
  return ['full_catalogue', 'starlink', 'oneweb', 'stations', 'gnss', 'sentinel', 'satnogs', 'aws_ground_station'].includes(
    network,
  );
}

/** Convenience selectors. */
export function selectMissionById(state: LoaderState, id: string | null): Mission | undefined {
  if (!id || !state.dataset) return undefined;
  return state.dataset.missions.find((m) => m.id === id);
}

export function selectActiveMissions(state: LoaderState): Mission[] {
  if (!state.dataset) return [];
  const active = new Set(state.activeMissionIds);
  return state.dataset.missions.filter((m) => active.has(m.id));
}
